import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { useEffect, useState, useMemo, type JSX } from 'react'
import config from '../../../../../config'
import {
  CancellationReasonInput,
  HostedLoginTokenDocument,
  ReasonEnum,
  RecurlySubscription,
  SubscriptionCancelDocument,
  SubscriptionDocument,
  SubscriptionReactivateDocument,
  SubscriptionsPaymentsDocument,
  UserProfile
} from '../../../../../graphql/generated'
import formatBytes from '../../../../../utils/formatBytes'
import logger from '../../../../../utils/logger'
import useGAEvent from '../../../../auth/hooks/useGAEvent'
import UpdatePaymentDialog from '../../../../subscription/components/payment-recurly/UpdatePaymentDialog'
import useAlerts from '../../../hooks/useAlerts'
import CancelModal from '../modals/CancelModal'
import ConfirmCancelModal from '../modals/ConfirmCancelModal'
import DowngradeModal from '../modals/DowngradeModal'
import GetHelpModal from '../modals/GetHelpModal'
import UpgradeModal from '../modals/UpgradeModal'
import { cancellationUrls, getPlanDisplayName, SHARING_TRIAL_PLAN } from '../utils/constants'

interface Props {
  userProfile: UserProfile
  loading: boolean
  openChangePlan: () => void
  recurlySubscription?: RecurlySubscription
  refetch?: () => void
}

export default function PlanDetails({ userProfile, loading, openChangePlan, recurlySubscription }: Readonly<Props>) {
  const { createAlert } = useAlerts()
  const [fetchHostedLoginTokenAndRedirect] = useLazyQuery(HostedLoginTokenDocument, {
    onCompleted: (data) => {
      window.open(`${config.appUrl.recurly.editPayment}${data.subscriptions.hostedLoginToken}`, '_blank')
    }
  })
  const [subscriptionCancelMutation] = useMutation(SubscriptionCancelDocument)
  const [subscriptionReactivedMutation] = useMutation(SubscriptionReactivateDocument)
  const subscriptionPaymentsResult = useQuery(SubscriptionsPaymentsDocument, {
    skip: !userProfile?.subscription?.id,
    variables: { subscriptionId: userProfile?.subscription?.id! },
    onError: (error) => {
      logger.error('Error fetching subscription payments:', error)
    }
  })

  const storageAvailable = userProfile?.plan?.storage_limit ?? 0
  const pendingPlan = getPlanDisplayName(recurlySubscription?.pendingChange?.plan?.code ?? 'no-plan')

  const trialPlan = userProfile?.subscription?.isTrial
  const trialExpirationDate = new Date(userProfile?.subscription?.trialExpDate ?? new Date())
  const formattedTrialExpirationDate = `${trialExpirationDate.getMonth() + 1}/${trialExpirationDate.getDate()}/${trialExpirationDate.getFullYear()}`
  const paymentMethod = userProfile.subscription?.subscription?.account ?? 'Free'
  const isApple = ['itunes', 'apple'].includes(paymentMethod.toLowerCase())
  const isPaypal = ['paypal', 'paypal_2', 'vindicia'].includes(paymentMethod.toLowerCase())
  const isRecurly = ['recurly'].includes(paymentMethod.toLowerCase())

  const subscriptionId = userProfile?.subscription?.subscription?.id
  const subscription = useQuery(SubscriptionDocument, {
    skip: !subscriptionId && !isRecurly,
    variables: { subscriptionId: subscriptionId! }
  })

  const currentTermEndsAt = subscription.data?.subscriptions?.subscription?.recurlySubscription?.currentTermEndsAt
  const currentPlan = userProfile?.subscription?.planName?.split('-')[0] ?? 'Free'

  const expirationDate = new Date(userProfile?.subscription?.expirationDate ?? '')
  const hasExpired = expirationDate < new Date()
  const formattedExpirationDate = trialPlan
    ? formattedTrialExpirationDate
    : `${expirationDate.getMonth() + 1}/${expirationDate.getDate()}/${expirationDate.getFullYear()}`
  let storageAvailableDisplay: JSX.Element | string
  const { trackEvent } = useGAEvent()

  const enabledInAppPaymentUpdate = useFlag('enabledInAppPaymentUpdate')

  switch (storageAvailable) {
    case -1:
      storageAvailableDisplay = <AllInclusiveIcon fontSize="medium" sx={{ ml: 1, mr: 1 }} />
      break
    case 0:
      storageAvailableDisplay = 'No Storage'
      break
    default:
      storageAvailableDisplay = formatBytes(storageAvailable)
      break
  }

  const isRecurring = userProfile?.subscription?.recurring

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false)
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState<boolean>(false)
  const [isGetHelpModalOpen, setIsGetHelpModalOpen] = useState<boolean>(false)
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] = useState<boolean>(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)
  const [cancellationReason, setCancellationReason] = useState<CancellationReasonInput | undefined>()
  const [isUpdatePaymentDialogOpen, setIsUpdatePaymentDialogOpen] = useState(false)

  useEffect(() => {
    if (cancellationReason) {
      if (cancellationReason.reason === ReasonEnum.HardToUse || cancellationReason.reason === ReasonEnum.Technical) {
        setIsGetHelpModalOpen(true)
      } else if (cancellationReason.reason === ReasonEnum.TooExpensive) {
        if (userProfile.plan?.is_storage_plan) {
          setIsConfirmCancelModalOpen(true)
        } else {
          setIsDowngradeModalOpen(true)
        }
      } else if (
        cancellationReason.reason === ReasonEnum.TemporaryUseCase ||
        cancellationReason.reason === ReasonEnum.Other ||
        cancellationReason.reason === ReasonEnum.DownloadContent ||
        cancellationReason.reason === ReasonEnum.MissingFeatures
      ) {
        if (userProfile.plan?.is_storage_plan) {
          setIsUpgradeModalOpen(true)
        } else {
          setIsDowngradeModalOpen(true)
        }
      }
    }
  }, [cancellationReason, userProfile.plan?.is_storage_plan])
  const planName = getPlanDisplayName(userProfile?.plan?.plan_name ?? 'no-plan')
  const [plan, cycle] = (userProfile?.plan?.plan_name ?? 'no-plan').split('-')
  const getPlanPrice = () => {
    if (cycle === 'trial') {
      return 0
    }
    if (cycle === 'annual') {
      return userProfile?.plan?.annual_price
    }
    return userProfile?.plan?.monthly_price
  }

  const planPrice = getPlanPrice()

  // Create display plan name with special case for sharing-trial when not on trial
  const displayPlanName = (() => {
    if (!userProfile?.subscription?.isTrial && planName === SHARING_TRIAL_PLAN) {
      return 'Group Buckets Monthly'
    }
    return planName
  })()

  const getStorageDisplay = () => {
    const storageLimit = userProfile.plan?.storage_limit

    if (storageLimit === -1) {
      return 'Unlimited Storage'
    }
    if (storageLimit !== null) {
      return `${formatBytes(storageLimit)} Compression Free Storage`
    }
    return 'Storage limit not set'
  }

  const paymentMethodText = useMemo(() => {
    if (!subscriptionPaymentsResult.data?.subscriptions?.payments?.paymentMethod?.object) {
      return null
    }

    const paymentMethodObject = subscriptionPaymentsResult.data.subscriptions.payments.paymentMethod.object
    const amount = trialPlan ? 8 : planPrice
    const nextBillingDate = formattedExpirationDate

    let paymentType = 'payment method'
    if (paymentMethodObject === 'credit_card') {
      paymentType = 'credit card'
    } else if (paymentMethodObject.includes('paypal')) {
      paymentType = 'PayPal account'
    } else if (paymentMethodObject.includes('apple')) {
      paymentType = 'Apple Pay account'
    } else if (paymentMethodObject === 'cash_app') {
      paymentType = 'Cash App account'
    }

    const cardSuffix =
      paymentMethodObject === 'credit_card' ? ` ending in ${subscriptionPaymentsResult.data?.subscriptions?.payments?.paymentMethod?.lastFour}` : ''
    const billingDate = trialPlan ? formattedTrialExpirationDate : nextBillingDate

    if (pendingPlan !== 'No Plan') {
      return (
        <>
          Your {paymentType}
          {cardSuffix} will be charged on <strong>{billingDate}</strong>
        </>
      )
    }

    if (!isRecurring) {
      return (
        <>
          Your {paymentType} {cardSuffix} will end on <strong>{billingDate}</strong>
        </>
      )
    }

    return (
      <>
        Your {paymentType}
        {cardSuffix} will be charged ${amount} on <strong>{billingDate}</strong>
      </>
    )
  }, [
    subscriptionPaymentsResult.data?.subscriptions?.payments?.paymentMethod?.object,
    subscriptionPaymentsResult.data?.subscriptions?.payments?.paymentMethod?.lastFour,
    trialPlan,
    planPrice,
    formattedExpirationDate,
    formattedTrialExpirationDate,
    pendingPlan,
    isRecurring
  ])

  const onCancelModalClose = (log = false) => {
    if (log) {
      trackEvent('cancel_membership_survey_cancel_close', {
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    }
    setIsCancelModalOpen(false)
  }

  const onUpgradeModalClose = (log = false) => {
    if (log) {
      trackEvent('cancel_membership_upgrade_cancel_close', {
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    }
    setIsUpgradeModalOpen(false)
  }

  const onUpgradeModalContinue = () => {
    trackEvent('cancel_membership_upgrade_cancel_continue', {
      username: userProfile?.id,
      plan: userProfile?.plan?.plan_name,
      recurring: userProfile?.subscription?.recurring
    })
    setIsUpgradeModalOpen(false)
    setTimeout(() => setIsConfirmCancelModalOpen(true), 1000)
  }

  const onDowngradeModalClose = (log = false) => {
    if (log) {
      trackEvent('cancel_membership_downgrade_cancel_close', {
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    }
    setIsDowngradeModalOpen(false)
  }

  const onDowngradeModalContinue = () => {
    trackEvent('cancel_membership_dowgrade_cancel_continue', {
      username: userProfile?.id,
      plan: userProfile?.plan?.plan_name,
      recurring: userProfile?.subscription?.recurring
    })
    setIsDowngradeModalOpen(false)
    setTimeout(() => setIsConfirmCancelModalOpen(true), 1000)
  }

  const onGetHelpModalClose = (log = false) => {
    if (log) {
      trackEvent('cancel_membership_get_help_cancel_close', {
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    }
    setIsGetHelpModalOpen(false)
  }

  const onGetHelpModalContinue = () => {
    trackEvent('cancel_membership_get_help_cancel_continue', {
      username: userProfile?.id,
      plan: userProfile?.plan?.plan_name,
      recurring: userProfile?.subscription?.recurring
    })
    setIsGetHelpModalOpen(false)
    setTimeout(() => setIsConfirmCancelModalOpen(true), 1000)
  }

  const onConfirmCancelModalClose = (log = false) => {
    if (log) {
      trackEvent('cancel_membership_confirm_cancel_close', {
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    }
    setIsConfirmCancelModalOpen(false)
  }

  const onReactivateSubscription = () => {
    trackEvent('cancel_reactivate_membership_clicked', {
      username: userProfile?.id,
      plan: userProfile?.plan?.plan_name,
      recurring: userProfile?.subscription?.recurring
    })
    if (subscriptionId) {
      const variables = {
        subscriptionId
      }
      subscriptionReactivedMutation({
        variables
      })
        .then(() => {
          createAlert('Your subscription has been reactivated successfully.', 'success')
        })
        .catch((error) => {
          createAlert('Failed to reactivate the subscription. Please try again.', 'error')
          logger.error('Error reactivating subscription:', error)
        })
    } else {
      createAlert('Subscription ID is missing. Unable to proceed with reactivation.', 'error')
    }
  }

  const onConfirmaCancelClick = () => {
    trackEvent('cancel_membership_confirm_cancel_clicked', {
      username: userProfile?.id,
      plan: userProfile?.plan?.plan_name,
      recurring: userProfile?.subscription?.recurring
    })
    if (subscriptionId) {
      const variables = {
        subscriptionId,
        input: {
          ...cancellationReason,
          createdBy: userProfile?.id ?? 'unknown',
          reason: cancellationReason?.reason ?? ReasonEnum.Other
        }
      }

      subscriptionCancelMutation({
        variables
      })
        .then(() => {
          createAlert('Your subscription has been canceled successfully.', 'success')
          // window.location.reload()
        })
        .catch((error) => {
          createAlert('Failed to cancel the subscription. Please try again.', 'error')
          logger.error('Error canceling subscription:', error)
        })
    } else {
      createAlert('Subscription ID is missing. Unable to proceed with cancellation.', 'error')
    }
    setIsConfirmCancelModalOpen(false)
  }

  const handleUpdatePayment = async () => {
    if (!subscriptionId) {
      createAlert('There was an issue fetching your profile. Please try again later.', 'error')
      return
    }

    if (enabledInAppPaymentUpdate) {
      setIsUpdatePaymentDialogOpen(true)
    } else {
      await fetchHostedLoginTokenAndRedirect({ variables: { subscriptionId } })
    }
  }

  const handleUpdatePaymentDialogClose = () => {
    setIsUpdatePaymentDialogOpen(false)
  }

  const handleUpdatePaypalPayment = () => {
    window.open(config.appUrl.paypal, '_blank')
  }

  // only for canceling recurly memberships
  const handleCancelPlanClick = () => {
    const planIdentifier = userProfile?.plan?.plan_name.toLowerCase()
    const redirectUrl = cancellationUrls[planIdentifier as keyof typeof cancellationUrls]

    if (redirectUrl) {
      setIsCancelModalOpen(true)

      trackEvent('cancel_membership_clicked', {
        variantName: 'cancel_membership_clicked',
        username: userProfile?.id,
        plan: userProfile?.plan?.plan_name,
        recurring: userProfile?.subscription?.recurring
      })
    } else {
      logger.error('No redirect URL found for the current plan:', planIdentifier)
    }
  }

  return (
    <>
      <div>
        <Typography variant="h6">{displayPlanName} Plan</Typography>
        {loading && <LinearProgress sx={{ mt: 3, maxWidth: '200px' }} />}
        {storageAvailable === -1 && <Typography style={{ marginTop: '10px' }}>Unlimited Storage</Typography>}
        {storageAvailable === 0 && <Typography sx={{ mt: 1, fontWeight: 600 }}>{storageAvailableDisplay}</Typography>}
        <Grid container style={{ margin: '20px 0', fontSize: '14px' }}>
          <Grid item xs={12} md={4} lg={3}>
            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {userProfile.plan?.plan_name !== 'no-plan' ? <li>{getStorageDisplay()}</li> : <li>You have no features associated with your plan.</li>}
              {userProfile.plan?.is_sharing_plan && <li>Unlimited Group & Personal Buckets</li>}
              {userProfile.plan?.plan_name !== 'no-plan' && !userProfile.plan?.is_sharing_plan && <li>Unlimited Personal Buckets</li>}
              {userProfile.plan?.is_modify_albums_enabled && <li>Advanced Album Organization</li>}
            </ul>
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {userProfile.plan?.plan_name !== 'no-plan' && <li>Free Facebook Integration</li>}
              {userProfile.plan?.is_image_editor_enabled && <li>Photo Image Editing</li>}
              {userProfile.plan?.is_video_player_enabled && <li>Video Playback</li>}
            </ul>
          </Grid>
        </Grid>
        {paymentMethodText && (
          <Typography sx={{ mt: 1, color: '#666' }}>
            {paymentMethodText}
            {pendingPlan !== 'No Plan' ? (
              <>
                {' '}
                as the <strong>{pendingPlan}</strong>.
              </>
            ) : (
              '.'
            )}
          </Typography>
        )}
        {isPaypal && (
          <Grid alignItems="center">
            <Button onClick={handleUpdatePaypalPayment} sx={{ display: 'block', mb: 2, p: 0 }} variant="text">
              Visit PayPal.com to view or update your payment method
            </Button>
          </Grid>
        )}
        {isApple && (
          <Typography style={{ paddingTop: 10 }}>
            Your subscription was started via Apple. To update your status or payment method, visit your iCloud subscriptions on your Apple device
          </Typography>
        )}
        {isRecurly && (
          <>
            <Button data-testid="view-or-update-payment-method" onClick={handleUpdatePayment} sx={{ display: 'block', mb: 2, p: 0 }} variant="text">
              View or update your payment method
            </Button>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              {isRecurring && (
                <Button
                  color="error"
                  variant="outlined"
                  data-testid="cancel-membership"
                  sx={{ width: 'fit-content' }}
                  onClick={handleCancelPlanClick}
                  disabled={Boolean(userProfile?.subscription?.isCanceled)}
                  data-pendo="cancel-membership--step-01"
                >
                  Cancel Membership
                </Button>
              )}
              {!isRecurring && !hasExpired && (
                <Button data-testid="reactivate-plan" onClick={onReactivateSubscription} variant="contained" sx={{ width: 'fit-content' }}>
                  Reactivate {displayPlanName} Plan
                </Button>
              )}
              {isRecurring && (
                <Button
                  data-testid="upgrade-or-change-plan"
                  onClick={openChangePlan}
                  variant="contained"
                  sx={{ width: 'fit-content' }}
                  data-pendo="upgrade-membership--step-01"
                >
                  Upgrade or Change Plan
                </Button>
              )}
              {userProfile?.subscription?.isCanceled && (
                <Button onClick={openChangePlan} variant="contained" sx={{ width: 'fit-content' }} data-pendo="resubscribe-membership--step-01">
                  Resubscribe
                </Button>
              )}
            </Box>
          </>
        )}
      </div>
      <CancelModal isOpen={isCancelModalOpen} onClose={onCancelModalClose} userProfile={userProfile} setCancellationReason={setCancellationReason} />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={onUpgradeModalClose}
        onContinue={onUpgradeModalContinue}
        currentTermEndsAt={currentTermEndsAt as string}
        createAlert={createAlert}
      />
      <DowngradeModal
        isOpen={isDowngradeModalOpen}
        onClose={onDowngradeModalClose}
        onContinue={onDowngradeModalContinue}
        currentPlanName={currentPlan}
        currentTermEndsAt={currentTermEndsAt as string}
        createAlert={createAlert}
      />
      <GetHelpModal
        isOpen={isGetHelpModalOpen}
        onClose={onGetHelpModalClose}
        onContinue={onGetHelpModalContinue}
        currentPlanName={currentPlan}
        currentTermEndsAt={currentTermEndsAt as string}
      />
      <ConfirmCancelModal
        isOpen={isConfirmCancelModalOpen}
        onClose={onConfirmCancelModalClose}
        confirmCancellation={onConfirmaCancelClick}
        subscriptionEndDate={formattedExpirationDate}
      />
      <UpdatePaymentDialog
        isOpen={isUpdatePaymentDialogOpen}
        onClose={handleUpdatePaymentDialogClose}
        planName={plan}
        billingCycle={cycle}
        total={planPrice?.toString() ?? '0.00'}
        billingDate={trialPlan ? formattedTrialExpirationDate : formattedExpirationDate}
      />
    </>
  )
}
